import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchMyPublicQuizSubmission,
  submitPublicQuiz,
  submitPublicQuizPut,
} from '@/features/webinar/webinarThunks';
import type { QuizTemplate } from '@/features/webinar/types';
import { toast } from 'react-toastify';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';

interface WaitingRoomQuizFormProps {
  waitingRoomQuiz: QuizTemplate;
  visualIdentity: WorkspaceVisualIdentity;
  theme: 'LIGHT' | 'DARK';
  token: string;
  setIsQuizActive: (isQuizActive: boolean) => void;
}

interface QuizFormData {
  [questionId: string]: string | string[];
}

export default function WaitingRoomQuizForm({
  waitingRoomQuiz,
  visualIdentity,
  theme,
  token,
  setIsQuizActive,
}: WaitingRoomQuizFormProps) {
  const [quizStep, setQuizStep] = useState(1);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasSubmittedRef = useRef(hasSubmitted);
  const lastSubmittedAnswerRef = useRef<string | string[] | null>(null);

  hasSubmittedRef.current = hasSubmitted;
  const [validatedQuestions, setValidatedQuestions] = useState<Set<number>>(new Set());
  const [validationResults, setValidationResults] = useState<{
    [questionIndex: number]: { isCorrect: boolean; correctAnswers: string[] };
  }>({});
  const dispatch = useAppDispatch();
  const publicSession = useAppSelector((state) => state.webinar.currentPublicSession);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const submitUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useWebinarT('webinars');
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<QuizFormData>({
    mode: 'onChange',
    defaultValues: {},
  });

  const watchedValues = watch();

  const validateQuestion = (questionIndex: number) => {
    const question = waitingRoomQuiz?.questions?.[questionIndex];
    if (!question) return;

    const answerValue = watchedValues[`question_${questionIndex}`];
    if (!answerValue) return;

    let isCorrect = false;
    const correctAnswers: string[] = [];

    if (question.type === 'SINGLE_CHOICE') {
      const selectedIndex = parseInt(answerValue as string);
      const selectedOption = question.options[selectedIndex];
      isCorrect = selectedOption?.correct || false;

      question.options.forEach((option) => {
        if (option.correct) {
          correctAnswers.push(option.textContent);
        }
      });
    } else {
      const selectedIndices = (answerValue as string[]).map((idx) => parseInt(idx));
      const selectedOptions = selectedIndices.map((idx) => question.options[idx]).filter(Boolean);

      const correctOptionIndices = question.options
        .map((opt, idx) => (opt.correct ? idx : -1))
        .filter((idx) => idx !== -1);

      const correctSelectedCount = selectedOptions.filter((opt) => opt.correct).length;
      isCorrect =
        correctSelectedCount === correctOptionIndices.length &&
        selectedOptions.length === correctOptionIndices.length;

      question.options.forEach((option) => {
        if (option.correct) {
          correctAnswers.push(option.textContent);
        }
      });
    }

    setValidationResults((prev) => ({
      ...prev,
      [questionIndex]: { isCorrect, correctAnswers },
    }));
    setValidatedQuestions((prev) => new Set(prev).add(questionIndex));
  };

  const handleNextQuestion = () => {
    const totalQuestions = waitingRoomQuiz?.questions?.length || 1;
    if (quizStep < totalQuestions) {
      setQuizStep(quizStep + 1);
    }
  };

  const totalQuestions = waitingRoomQuiz?.questions?.length ?? 0;
  const lastQuestionIndex = totalQuestions > 0 ? totalQuestions - 1 : -1;
  const isLastQuestion = lastQuestionIndex >= 0 && quizStep - 1 === lastQuestionIndex;

  const clearSubmitUiTimer = () => {
    if (submitUiTimerRef.current != null) {
      clearTimeout(submitUiTimerRef.current);
      submitUiTimerRef.current = null;
    }
  };

  const onFormSubmit = async (data: QuizFormData) => {
    if (!waitingRoomQuiz?.questions || !token || isSubmittingLocal) return;

    const sessionId = publicSession?.session.id;
    if (!sessionId) {
      console.error('Impossible de soumettre le quiz : sessionId manquant');
      return;
    }

    clearSubmitUiTimer();
    const submitStartedAt = Date.now();
    setIsSubmittingLocal(true);

    try {
      const answersPayload = waitingRoomQuiz.questions.map((item, index) => {
        const questionKey = `question_${index}`;
        const answers = data[questionKey];

        let optionIds: number[] = [];

        if (item.type === 'SINGLE_CHOICE') {
          const answerIndex = parseInt(answers as string);
          const selectedOption = item.options[answerIndex];
          if (selectedOption) {
            optionIds = [selectedOption.id];
          }
        } else {
          const answerIndices = (answers as string[]).map((idx) => parseInt(idx));
          optionIds = answerIndices
            .map((idx) => item.options[idx])
            .filter((opt): opt is (typeof item.options)[number] => !!opt)
            .map((opt) => opt.id);
        }

        return {
          questionId: item.id,
          optionIds,
        };
      });

      const payload = {
        webinarId: waitingRoomQuiz.webinarId,
        templateId: waitingRoomQuiz.id,
        data: {
          sessionId,
          answers: answersPayload,
        },
        authorization: token,
      };

      if (hasSubmitted) {
        await dispatch(submitPublicQuizPut(payload)).unwrap();
        toast.success(t('waitingRoom.quiz.saveSuccess'));
      } else {
        await dispatch(submitPublicQuiz(payload)).unwrap();
        setHasSubmitted(true);
        toast.success(t('waitingRoom.quiz.saveSuccess'));
      }

      const elapsedMs = Date.now() - submitStartedAt;
      const remainingMs = Math.max(0, 5000 - elapsedMs);
      submitUiTimerRef.current = setTimeout(() => {
        setIsSubmittingLocal(false);
        if (isLastQuestion) {
          setIsQuizActive(false);
        }
        submitUiTimerRef.current = null;
      }, remainingMs);
    } catch (error) {
      console.error('Erreur lors de la soumission du quiz:', error);
      setIsSubmittingLocal(false);
      if (isLastQuestion) {
        lastSubmittedAnswerRef.current = null;
      }
    }
  };

  // Dernière question : sauvegarde automatique (pas de bouton Valider)
  useEffect(() => {
    if (!isLastQuestion || lastQuestionIndex < 0) return;

    const lastKey = `question_${lastQuestionIndex}`;
    const value = watchedValues[lastKey];
    const isValid =
      value !== undefined &&
      value !== null &&
      (Array.isArray(value) ? value.length > 0 : value !== '');

    if (!isValid) return;

    const valueKey = JSON.stringify(value);
    if (lastSubmittedAnswerRef.current === valueKey) return;
    lastSubmittedAnswerRef.current = valueKey;

    validateQuestion(lastQuestionIndex);
    handleSubmit(onFormSubmit)();
  }, [isLastQuestion, lastQuestionIndex, watchedValues[`question_${lastQuestionIndex}`]]);

  useEffect(() => {
    return () => {
      clearSubmitUiTimer();
      if (
        hasSubmittedRef.current &&
        waitingRoomQuiz?.webinarId != null &&
        waitingRoomQuiz?.id != null &&
        token
      ) {
        dispatch(
          fetchMyPublicQuizSubmission({
            webinarId: waitingRoomQuiz.webinarId,
            templateId: waitingRoomQuiz.id,
            authorization: token,
          }),
        );
      }
    };
  }, [dispatch, waitingRoomQuiz?.webinarId, waitingRoomQuiz?.id, token]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-4 w-full">
      <h3
        className="text-xl font-medium"
        style={{
          color: theme === 'DARK' ? 'white' : 'black',
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {waitingRoomQuiz?.name || t('waitingRoom.quiz.defaultTitle')}
      </h3>

      <p
        className="text-sm"
        style={{
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
          color: theme === 'DARK' ? 'white' : '#6D6E7A',
        }}
      >
        {waitingRoomQuiz?.subTitle || t('waitingRoom.quiz.defaultSubtitle')}
      </p>

      <div className="flex flex-col gap-4">
        {waitingRoomQuiz?.questions?.map((question: any, realIndex: number) => {
          if (realIndex !== quizStep - 1) return null;

          return (
            <div key={realIndex}>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  {/* {quizStep > 1 && (
                    <ArrowLeftIcon
                      className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                      onClick={() => {
                        setQuizStep(quizStep - 1);
                      }}
                    />
                  )} */}
                  <h3
                    className="text-base font-medium"
                    style={{
                      color: theme === 'DARK' ? 'white' : 'black',
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    <span
                      style={{
                        color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {quizStep}/{waitingRoomQuiz?.questions?.length || 1}
                    </span>{' '}
                    {question.textContent}
                  </h3>
                </div>
                {/* {quizStep < (waitingRoomQuiz?.questions?.length || 1) && (
                  <ArrowRightIcon
                    className="size-6 text-[#9E9FA6] cursor-pointer"
                    onClick={() => {
                      setQuizStep(quizStep + 1);
                    }}
                  />
                )} */}
              </div>
              <div className="flex flex-col gap-2 pt-4">
                {question.type === 'SINGLE_CHOICE' ? (
                  <Controller
                    name={`question_${realIndex}`}
                    control={control}
                    rules={{
                      required: t('waitingRoom.quiz.validation.questionRequired') as string,
                    }}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value as string}
                        onValueChange={(value) => {
                          const locked =
                            (validatedQuestions.has(realIndex) &&
                              realIndex !== lastQuestionIndex) ||
                            isSubmittingLocal;
                          if (!locked) {
                            field.onChange(value);
                          }
                        }}
                        disabled={
                          (validatedQuestions.has(realIndex) && realIndex !== lastQuestionIndex) ||
                          isSubmittingLocal
                        }
                      >
                        {question.options.map((option: any, optionIndex: number) => {
                          const isSelected = field.value === optionIndex.toString();
                          const isCorrect = option.correct;
                          const isWrong =
                            isSelected && !isCorrect && validatedQuestions.has(realIndex);
                          const isRight =
                            isSelected && isCorrect && validatedQuestions.has(realIndex);

                          const rowLockedByStep =
                            validatedQuestions.has(realIndex) && realIndex !== lastQuestionIndex;
                          const rowInteractionLocked = rowLockedByStep || isSubmittingLocal;

                          return (
                            <label
                              key={optionIndex}
                              className={`flex items-center gap-2 p-2 rounded-lg w-full ${
                                rowInteractionLocked ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                              style={{
                                backgroundColor:
                                  theme === 'LIGHT'
                                    ? 'rgba(31, 31, 31, 0.03)'
                                    : 'rgba(255, 255, 255, 0.1)',
                                border: validatedQuestions.has(realIndex)
                                  ? isRight
                                    ? '2px solid #22c55e'
                                    : isWrong
                                      ? '2px solid #ef4444'
                                      : isCorrect
                                        ? '2px solid #22c55e'
                                        : 'none'
                                  : 'none',
                              }}
                            >
                              <RadioGroupItem
                                value={optionIndex.toString()}
                                className={`size-6 shrink-0 ${rowInteractionLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                disabled={rowInteractionLocked}
                              />
                              <span
                                className="text-base flex-1 text-left"
                                style={{
                                  color: theme === 'DARK' ? 'white' : 'black',
                                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                }}
                              >
                                {option.textContent}
                              </span>
                            </label>
                          );
                        })}
                      </RadioGroup>
                    )}
                  />
                ) : (
                  // Checkboxes pour plusieurs réponses
                  <Controller
                    name={`question_${realIndex}`}
                    control={control}
                    rules={{
                      required: t('waitingRoom.quiz.validation.minAnswersRequired') as string,
                    }}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        {question.options.map((option: any, optionIndex: number) => {
                          const isSelected =
                            (field.value as string[])?.includes(optionIndex.toString()) || false;
                          const isCorrect = option.correct;
                          const isWrong =
                            isSelected && !isCorrect && validatedQuestions.has(realIndex);
                          const isRight =
                            isSelected && isCorrect && validatedQuestions.has(realIndex);

                          return (
                            <div
                              key={optionIndex}
                              className="flex items-center gap-2 p-2 rounded-lg"
                              style={{
                                backgroundColor:
                                  theme === 'LIGHT'
                                    ? 'rgba(31, 31, 31, 0.03)'
                                    : 'rgba(255, 255, 255, 0.1)',
                                border: validatedQuestions.has(realIndex)
                                  ? isRight
                                    ? '2px solid #22c55e'
                                    : isWrong
                                      ? '2px solid #ef4444'
                                      : isCorrect
                                        ? '2px solid #22c55e'
                                        : 'none'
                                  : 'none',
                              }}
                            >
                              <Checkbox
                                className={`size-6 ${
                                  (validatedQuestions.has(realIndex) &&
                                    realIndex !== lastQuestionIndex) ||
                                  isSubmittingLocal
                                    ? 'cursor-not-allowed opacity-60'
                                    : 'cursor-pointer'
                                }`}
                                checked={isSelected}
                                disabled={
                                  (validatedQuestions.has(realIndex) &&
                                    realIndex !== lastQuestionIndex) ||
                                  isSubmittingLocal
                                }
                                onCheckedChange={(checked) => {
                                  const locked =
                                    (validatedQuestions.has(realIndex) &&
                                      realIndex !== lastQuestionIndex) ||
                                    isSubmittingLocal;
                                  if (!locked) {
                                    const currentValues = (field.value as string[]) || [];
                                    if (checked) {
                                      field.onChange([...currentValues, optionIndex.toString()]);
                                    } else {
                                      field.onChange(
                                        currentValues.filter(
                                          (val: string) => val !== optionIndex.toString(),
                                        ),
                                      );
                                    }
                                  }
                                }}
                              />
                              <p
                                className="text-base"
                                style={{
                                  color: theme === 'DARK' ? 'white' : 'black',
                                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                                }}
                              >
                                {option.textContent}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  />
                )}
                {errors[`question_${realIndex}`] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[`question_${realIndex}`]?.message}
                  </p>
                )}

                {/* Affichage du feedback après validation */}
                {validatedQuestions.has(realIndex) && validationResults[realIndex] && (
                  <div
                    className="mt-4 p-4 rounded-lg"
                    style={{
                      backgroundColor: validationResults[realIndex].isCorrect
                        ? theme === 'DARK'
                          ? 'rgba(34, 197, 94, 0.2)'
                          : 'rgba(34, 197, 94, 0.1)'
                        : theme === 'DARK'
                          ? 'rgba(239, 68, 68, 0.2)'
                          : 'rgba(239, 68, 68, 0.1)',
                    }}
                  >
                    <p
                      className="font-medium mb-2"
                      style={{
                        color: validationResults[realIndex].isCorrect ? '#22c55e' : '#ef4444',
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {validationResults[realIndex].isCorrect
                        ? t('waitingRoom.quiz.correctAnswer')
                        : t('waitingRoom.quiz.wrongAnswer')}
                    </p>
                    {!validationResults[realIndex].isCorrect && (
                      <div>
                        <p
                          className="text-sm mb-1"
                          style={{
                            color: theme === 'DARK' ? 'white' : 'black',
                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                          }}
                        >
                          {t('waitingRoom.quiz.theCorrectAnswerIs')}
                        </p>
                        <ul className="list-disc list-inside">
                          {validationResults[realIndex].correctAnswers.map((answer) => (
                            <li
                              key={answer}
                              className="text-sm"
                              style={{
                                color: theme === 'DARK' ? 'white' : 'black',
                                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                              }}
                            >
                              {answer}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Bouton Valider/Suivant : pas de bouton pour la dernière question (sauvegarde auto) */}
                {realIndex !== lastQuestionIndex && (
                  <div className="flex justify-center pt-4">
                    {validatedQuestions.has(realIndex) ? (
                      <Button
                        type="button"
                        onClick={handleNextQuestion}
                        disabled={isSubmittingLocal}
                        style={{
                          backgroundColor: visualIdentity?.accentColor || undefined,
                          color: 'black',
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {t('waitingRoom.quiz.next')}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => validateQuestion(realIndex)}
                        disabled={!watchedValues[`question_${realIndex}`] || isSubmittingLocal}
                        style={{
                          backgroundColor: visualIdentity?.accentColor || undefined,
                          color: 'black',
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                          cursor: !watchedValues[`question_${realIndex}`]
                            ? 'not-allowed'
                            : 'pointer',
                        }}
                      >
                        {t('waitingRoom.quiz.validate')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </form>
  );
}
